import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { JobCounters } from '../../registry';
import {
    Detail,
    DetailSummaryKey,
    DetailSummaryTypes,
    DetailType,
    JobDataItem,
    JobResultView,
    SapHierarchyJobData,
} from '../../types';

type JobData = {
    aggregateId?: string;
    createdNodes: (JobDataItem | string)[];
    itemsWithPricingChanges: (JobDataItem | string)[];
    itemsWithNonRelevantChanges: (JobDataItem | string)[];
    itemsModified: (JobDataItem | string)[];
    itemsNotModifiedCount: number;
    itemsDisabled: (JobDataItem | string)[];
    alignedModels: (JobDataItem | string)[];
    alignedGls: (JobDataItem | string)[];
    itemsNotModified: (JobDataItem | string)[];
    updatedGls: (JobDataItem | string)[];
    updatedModels: (JobDataItem | string)[];
    createdFamilies: (JobDataItem | string)[];
    createdModels: (JobDataItem | string)[];
    itemsWithBlockedPriceUpdate: (JobDataItem | string)[];
    itemsWithBlockedNotChargedUpdate: (JobDataItem | string)[];
    removedSchede: (JobDataItem | string)[];
    republishedSchede: (JobDataItem | string)[];
};

/** Old job documents may have no logs at all; reading them directly throws. */
export function getJobLogs(job: JobResultView) {
    const { infos = [], warnings = [], errors = [] } = job.logs ?? {};
    return { infos, warnings, errors };
}

export function getJobData(job: JobResultView) {
    const { type: jobType, data } = job;

    let jobData: JobData = {
        createdNodes: [],
        itemsWithPricingChanges: [],
        itemsWithNonRelevantChanges: [],
        itemsModified: [],
        itemsNotModifiedCount: 0,
        itemsDisabled: [],
        alignedModels: [],
        alignedGls: [],
        itemsNotModified: [],
        updatedGls: [],
        updatedModels: [],
        createdFamilies: [],
        createdModels: [],
        itemsWithBlockedPriceUpdate: [],
        itemsWithBlockedNotChargedUpdate: [],
        removedSchede: [],
        republishedSchede: [],
    };

    switch (jobType) {
        case 'import':
            jobData = {
                ...jobData,
                ...data,
                ...(job.data && 'cgtRecap' in job.data && job.data?.cgtRecap
                    ? job.data.cgtRecap
                    : {}),
            };
            break;
        case 'AlignPrices':
        case 'CatImport':
        case 'cat-import':
        case 'cgt-import-ricambi':
        case 'import-idp-users':
        case 'restart-translation':
        case 'find-replace-apply':
        case 'bulk-change-rule':
        case 'cgt-import-sap-mm-dispo':
        case 'publish-family':
        case 'close-family':
        case 'reopen-family': {
            jobData = { ...jobData, ...data };
            break;
        }
        case 'cgt-import-sap-hierarchy': {
            jobData = {
                ...jobData,
                createdNodes: (data?.createdNodes ?? []).map(item => ({
                    hierarchyId: item.hierarchyId,
                    nodeId: item.nodeId,
                    code: item.code,
                    changes: [],
                })),
            };
            break;
        }
        case 'duplicate-node': {
            // Backward compat: old jobs store { hierarchyId: string, createdNodes: string[] }
            if (data?.createdNodes?.length && typeof data.createdNodes[0] === 'string') {
                jobData = {
                    ...jobData,
                    createdNodes: (data.createdNodes as unknown as string[]).map(nodeId => ({
                        hierarchyId:
                            ((data as Record<string, unknown>).hierarchyId as string) ?? '',
                        nodeId,
                        code: '',
                        changes: [],
                    })),
                };
            } else {
                jobData = { ...jobData, ...data };
            }
            break;
        }
        default:
            break;
    }

    return jobData;
}

/**
 * Every counter the overview can display, computed once per job. The formulas are carried over
 * verbatim from the previous JobOverview implementation so the numbers users already know do
 * not change.
 */
export function getJobCounters(job: JobResultView, priceListHierarchyId?: string): JobCounters {
    const jobData = getJobData(job);
    const { warnings, errors } = getJobLogs(job);

    const showDetailed =
        job.type === 'CatImport' ||
        job.type === 'cat-import' ||
        job.type === 'cgt-import-ricambi' ||
        (!!priceListHierarchyId && jobData.aggregateId === priceListHierarchyId);

    const created = jobData.createdNodes.length;
    const modified =
        (jobData.itemsModified?.length || jobData.itemsWithNonRelevantChanges?.length) ?? 0;
    const notModified =
        (showDetailed ? jobData.itemsNotModifiedCount : jobData.itemsNotModified.length) ?? 0;

    return {
        [DetailType.CREATED]: created,
        [DetailType.CREATED_MODIFIED]: created + modified,
        [DetailType.PRICING_CHANGES]: jobData.itemsWithPricingChanges.length,
        [DetailType.NON_RELEVANT_CHANGES]: jobData.itemsWithNonRelevantChanges.length,
        [DetailType.NON_MODIFIED]: notModified,
        [DetailType.DISABLED]: jobData.itemsDisabled.length,
        [DetailType.ALIGNED_MODELS]: jobData.alignedModels.length,
        [DetailType.ALIGNED_GLS]: jobData.alignedGls.length,
        [DetailType.UPDATED_GLS]: jobData.updatedGls.length,
        [DetailType.UPDATED_MODELS]: jobData.updatedModels.length,
        [DetailType.CREATED_FAMILIES]: jobData.createdFamilies.length,
        [DetailType.CREATED_MODELS]: jobData.createdModels.length,
        [DetailType.REMOVED_SCHEDE]: jobData.removedSchede.length,
        [DetailType.REPUBLISHED_SCHEDE]: jobData.republishedSchede.length,
        [DetailType.BLOCKED_PRICE_UPDATE]: jobData.itemsWithBlockedPriceUpdate.length,
        [DetailType.BLOCKED_NOT_CHARGED_UPDATE]: jobData.itemsWithBlockedNotChargedUpdate.length,
        [DetailType.NEW_FAMILIES_ADDED]:
            job.type === 'cgt-import-sap-hierarchy'
                ? (job.data as SapHierarchyJobData)?.newFamiliesAdded?.length ?? 0
                : 0,
        [DetailType.REALIGNED_FAMILIES]:
            job.type === 'cgt-import-sap-hierarchy'
                ? (job.data as SapHierarchyJobData)?.realignedFamilies?.length ?? 0
                : 0,
        [DetailType.WARNINGS]: warnings.length,
        [DetailType.FAILURES]: errors.length,
    };
}

function detailDescription(keyword: ReactNode, type: DetailSummaryKey, count: number) {
    // The messages below are worded for imports; a job that is not one describes the nodes it
    // touched without claiming an import happened.
    if (type === DetailType.REMOVED_SCHEDE || type === DetailType.REPUBLISHED_SCHEDE) {
        return (
            <FormattedMessage
                id="job-detail.node-info"
                defaultMessage="The following <strong>{count}</strong> {count, plural, one {node} other {nodes}} {keyword}:"
                values={{
                    keyword,
                    count,
                    // eslint-disable-next-line react/no-unstable-nested-components
                    strong: str => <strong>{str}</strong>,
                }}
            />
        );
    }

    if (
        [
            DetailType.CREATED_MODIFIED,
            DetailType.NON_MODIFIED,
            DetailType.CREATED,
            DetailType.DISABLED,
            DetailType.NON_RELEVANT_CHANGES,
            DetailType.PRICING_CHANGES,
        ].includes(type)
    ) {
        return (
            <FormattedMessage
                id="import-job-detail.node-info"
                defaultMessage="The following <strong>{count}</strong> {count, plural, one {node} other {nodes}} {keyword} during import:"
                values={{
                    keyword,
                    count,
                    // eslint-disable-next-line react/no-unstable-nested-components
                    strong: str => <strong>{str}</strong>,
                }}
            />
        );
    }

    // No {plural} placeholder: it used to be filled with a hardcoded English 's', which every
    // other language inherited ("errores"). The keyword pluralizes itself instead.
    return (
        <FormattedMessage
            id="import-job-detail.errors-info"
            defaultMessage="The following <strong>{count}</strong> {keyword} were generated during import:"
            values={{
                keyword,
                count,
                // eslint-disable-next-line react/no-unstable-nested-components
                strong: str => <strong>{str}</strong>,
            }}
        />
    );
}

export function detailTypes(
    job: JobResultView,
    type: DetailType,
): DetailSummaryTypes[DetailSummaryKey] {
    const jobData = getJobData(job);
    const jobType = job.type;

    const { warnings, errors } = getJobLogs(job);

    let descKeyword: ReactNode;
    let details: (string | JobDataItem)[];

    switch (type) {
        case DetailType.CREATED_MODIFIED:
            descKeyword = (
                <FormattedMessage
                    id="import-job-detail.keyword.created-modified"
                    defaultMessage="were created and/or modified"
                />
            );
            details = [
                ...jobData.createdNodes,
                ...jobData.itemsWithPricingChanges,
                ...jobData.itemsWithNonRelevantChanges,
                ...jobData.itemsModified,
            ];
            break;
        case DetailType.CREATED:
            descKeyword = (
                <FormattedMessage
                    id="import-job-detail.keyword.created"
                    defaultMessage="were created"
                />
            );
            details = jobData.createdNodes;
            break;
        case DetailType.PRICING_CHANGES:
            descKeyword = (
                <FormattedMessage
                    id="import-job-detail.keyword.pricing-changes"
                    defaultMessage="had price changes"
                />
            );
            details = jobData.itemsWithPricingChanges;
            break;
        case DetailType.NON_RELEVANT_CHANGES:
            descKeyword = (
                <FormattedMessage
                    id="import-job-detail.keyword.non-relevant-changes"
                    defaultMessage="had non relevant changes"
                />
            );
            details = jobData.itemsWithNonRelevantChanges;
            break;
        case DetailType.DISABLED:
            descKeyword = (
                <FormattedMessage
                    id="import-job-detail.keyword.deactivated"
                    defaultMessage="were deactivated"
                />
            );
            details = jobData.itemsDisabled;
            break;
        // The keywords below pluralize themselves: `details` is therefore assigned first, so
        // its length can be handed to the message.
        case DetailType.ALIGNED_GLS:
            details = jobData.alignedGls;
            descKeyword = (
                <FormattedMessage
                    id="import-job-detail.keyword.aligned-gl"
                    defaultMessage="{count, plural, one {aligned GL} other {aligned GLs}}"
                    values={{ count: details.length }}
                />
            );
            break;
        case DetailType.ALIGNED_MODELS:
            details = jobData.alignedModels;
            descKeyword = (
                <FormattedMessage
                    id="import-job-detail.keyword.aligned-model"
                    defaultMessage="{count, plural, one {aligned model} other {aligned models}}"
                    values={{ count: details.length }}
                />
            );
            break;
        case DetailType.UPDATED_GLS:
            details = jobData.updatedGls;
            descKeyword = (
                <FormattedMessage
                    id="import-job-detail.keyword.updated-gl"
                    defaultMessage="{count, plural, one {updated GL} other {updated GLs}}"
                    values={{ count: details.length }}
                />
            );
            break;
        case DetailType.UPDATED_MODELS:
            details = jobData.updatedModels;
            descKeyword = (
                <FormattedMessage
                    id="import-job-detail.keyword.updated-model"
                    defaultMessage="{count, plural, one {updated model} other {updated models}}"
                    values={{ count: details.length }}
                />
            );
            break;
        case DetailType.NEW_FAMILIES_ADDED:
            descKeyword = (
                <FormattedMessage
                    id="import-job-detail.keyword.new-families-added"
                    defaultMessage="new families added to object schema"
                />
            );
            details = ((job.data as SapHierarchyJobData)?.newFamiliesAdded ?? []).map(
                f => `${f.code} — ${f.description}`,
            );
            break;
        case DetailType.REALIGNED_FAMILIES:
            descKeyword = (
                <FormattedMessage
                    id="import-job-detail.keyword.realigned-families"
                    defaultMessage="had their family realigned"
                />
            );
            details = ((job.data as SapHierarchyJobData)?.realignedFamilies ?? []).map(f => ({
                nodeId: f.nodeId,
                hierarchyId: f.hierarchyId,
                code: f.code,
                changes: [`${f.previous || '—'} → ${f.current}`],
            }));
            break;
        case DetailType.REMOVED_SCHEDE:
            descKeyword = (
                <FormattedMessage
                    id="job-detail.keyword.removed-schede"
                    defaultMessage="had their scheda removed from Coolsales"
                />
            );
            details = jobData.removedSchede;
            break;
        case DetailType.REPUBLISHED_SCHEDE:
            descKeyword = (
                <FormattedMessage
                    id="job-detail.keyword.republished-schede"
                    defaultMessage="had their scheda republished on Coolsales"
                />
            );
            details = jobData.republishedSchede;
            break;
        case DetailType.NON_MODIFIED:
            descKeyword = (
                <FormattedMessage
                    id="import-job-detail.keyword.not-modified"
                    defaultMessage="were not modified"
                />
            );
            details = jobData.itemsNotModified;
            break;
        case DetailType.CREATED_FAMILIES:
            descKeyword = (
                <FormattedMessage
                    id="import-job-detail.keyword.created-families"
                    defaultMessage="families were created"
                />
            );
            details = jobData.createdFamilies;
            break;
        case DetailType.CREATED_MODELS:
            descKeyword = (
                <FormattedMessage
                    id="import-job-detail.keyword.created-models"
                    defaultMessage="models were created"
                />
            );
            details = jobData.createdModels;
            break;
        case DetailType.BLOCKED_PRICE_UPDATE:
            descKeyword = (
                <FormattedMessage
                    id="import-job-detail.keyword.blocked-price-update"
                    defaultMessage="had their price update blocked"
                />
            );
            details = jobData.itemsWithBlockedPriceUpdate;
            break;
        case DetailType.BLOCKED_NOT_CHARGED_UPDATE:
            descKeyword = (
                <FormattedMessage
                    id="import-job-detail.keyword.blocked-not-charged-update"
                    defaultMessage="had their not-charged update blocked"
                />
            );
            details = jobData.itemsWithBlockedNotChargedUpdate;
            break;
        case DetailType.WARNINGS:
            details = warnings;
            descKeyword = (
                <FormattedMessage
                    id="import-job-detail.keyword.warning"
                    defaultMessage="{count, plural, one {warning} other {warnings}}"
                    values={{ count: details.length }}
                />
            );
            break;
        case DetailType.FAILURES:
            details = errors;
            descKeyword = (
                <FormattedMessage
                    id="import-job-detail.keyword.error"
                    defaultMessage="{count, plural, one {error} other {errors}}"
                    values={{ count: details.length }}
                />
            );
            break;
        default:
            // Reached when an old job is opened on a detail type this build no longer knows.
            // Throwing here would happen during render and take down the whole dialog.
            return {
                descriptionComponent: (
                    <FormattedMessage
                        id="job.detail.unknown"
                        defaultMessage="No details available for this section."
                    />
                ),
                details: [],
            };
    }

    return {
        descriptionComponent: detailDescription(descKeyword, type, details.length),
        details:
            typeof details[0] === 'string'
                ? (details as string[])
                : (details as JobDataItem[]).flatMap<Detail>(detail => {
                      const { hierarchyId, nodeId, code, changes, isReplacement, ...rest } =
                          detail && typeof detail !== 'string'
                              ? detail
                              : {
                                    hierarchyId: '',
                                    nodeId: '',
                                    code: '',
                                    isReplacement: false,
                                    changes: [detail],
                                };
                      const returnedChanges = changes && changes.length === 0 ? [''] : changes;
                      return type === DetailType.CREATED ||
                          type === DetailType.UPDATED_MODELS ||
                          type === DetailType.UPDATED_GLS ||
                          type === DetailType.NON_MODIFIED ||
                          type === DetailType.DISABLED ||
                          type === DetailType.REMOVED_SCHEDE ||
                          type === DetailType.REPUBLISHED_SCHEDE
                          ? [
                                {
                                    hierarchyId,
                                    nodeId,
                                    code,
                                    ...(jobType !== 'AlignPrices' && {
                                        change:
                                            type === DetailType.CREATED ||
                                            type === DetailType.UPDATED_GLS ||
                                            type === DetailType.REMOVED_SCHEDE ||
                                            type === DetailType.REPUBLISHED_SCHEDE
                                                ? returnedChanges[0]
                                                : null,
                                    }),
                                    isReplacement,
                                    ...rest,
                                },
                            ]
                          : returnedChanges?.map(change => ({
                                hierarchyId,
                                nodeId,
                                code,
                                change,
                                isReplacement,
                            }));
                  }),
    };
}
